/**
 * Flipbook mode for the Animation panel — a second surface onto the SAME
 * flipbook the bottom filmstrip (FlipbookBar) drives. Every control reads and
 * writes the shared FlipbookContext, and page mutations go through the shared
 * useFlipbookActions hook, so the two views stay in lockstep with no second
 * implementation of playback, onion skin, or page management.
 *
 * FlipbookBar is always mounted, so its playback and onion effects react to the
 * context values this panel writes — the panel itself needs no playback loop
 * and no onion rendering.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";

import type { ExcalidrawFrameLikeElement } from "@excalidraw/element/types";

import { applyBake } from "../flipbook/applyBake";
import { bookTargetId } from "../animation/export/targets";
import {
  clearPageIds,
  deletePageIds,
  flipbookPageCustomData,
  getFlipbookBooks,
  getFlipbookPages,
  getScreens,
  planAdoptFrames,
} from "../flipbook/ops";
import { useFlipbook } from "../flipbook/FlipbookContext";
import {
  resolveBookId,
  useFlipbookActions,
} from "../flipbook/useFlipbookActions";

import { FloatingMenu, FrameThumb } from "./FlipbookBar";

import "./FlipbookPanel.scss";

import type { PlayMode } from "../flipbook/FlipbookContext";

const PLAY_MODES: { id: PlayMode; label: string }[] = [
  { id: "loop", label: "Loop" },
  { id: "pingpong", label: "Ping-pong" },
  { id: "once", label: "Play once" },
];

// [− value +] group at input-field metrics. The value stays a real number
// input so typing works; ± clicks commit immediately, typing commits on blur.
const Stepper = ({
  value,
  min,
  max,
  disabled,
  id,
  onValue,
  onCommit,
}: {
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  id?: string;
  onValue: (v: number) => void;
  onCommit?: (v: number) => void;
}) => {
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v)));
  const step = (delta: number) => {
    const next = clamp(value + delta);
    onValue(next);
    onCommit?.(next);
  };
  return (
    <span
      className={`flipbook-stepper${
        disabled ? " flipbook-stepper--disabled" : ""
      }`}
    >
      <button
        type="button"
        aria-label="Decrease"
        disabled={disabled || value <= min}
        onClick={() => step(-1)}
      >
        −
      </button>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (e.target.value !== "" && Number.isFinite(v)) {
            // clamp as typed, not just on blur: fps drives the live playback
            // interval and the export hold, and "0" between keystrokes means
            // a 1000/0 = Infinity frame
            onValue(clamp(v));
          }
        }}
        onBlur={(e) => {
          const n = Number(e.target.value);
          const v = clamp(Number.isFinite(n) ? n : value);
          onValue(v);
          onCommit?.(v);
        }}
      />
      <button
        type="button"
        aria-label="Increase"
        disabled={disabled || value >= max}
        onClick={() => step(1)}
      >
        +
      </button>
    </span>
  );
};

export const FlipbookPanel = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const appState = useUIAppState();
  const {
    setOpen,
    setFlipbookActive,
    activeId,
    setActiveId,
    playing,
    setPlaying,
    playMode,
    setPlayMode,
    onion,
    setOnion,
    fps,
    setFps,
    activeBookId,
    setActiveBookId,
    setExportTargetId,
  } = useFlipbook();
  const {
    goTo,
    addPage,
    addBook,
    removeBook,
    duplicatePage,
    removeIds,
    applyFps,
    reorderPages,
    captureUndo,
    withGhostsSuspended,
  } = useFlipbookActions(excalidrawAPI);

  const [dragId, setDragId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [menu, setMenu] = useState<{
    frameId: string;
    x: number;
    y: number;
  } | null>(null);
  const [screenPicker, setScreenPicker] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [bakeNotice, setBakeNotice] = useState<string | null>(null);
  // deleting a book takes every page and everything drawn on them with it —
  // armed per book id, so switching books disarms it on its own
  const [confirmDeleteBook, setConfirmDeleteBook] = useState<string | null>(
    null,
  );
  const closeMenu = useCallback(() => setMenu(null), []);
  const closeScreenPicker = useCallback(() => setScreenPicker(null), []);
  // book switcher popover — anchored under its button rather than at a cursor
  const [bookMenu, setBookMenu] = useState(false);
  const bookMenuAnchor = useRef<HTMLButtonElement>(null);
  const closeBookMenu = useCallback(() => setBookMenu(false), []);
  const bookMenuOrigin = () => {
    const r = bookMenuAnchor.current?.getBoundingClientRect();
    return { x: r?.left ?? 0, y: (r?.bottom ?? 0) + 6 };
  };

  // thumbnails/pages derive from scene elements; subscribe to scene changes so
  // the grid, adopt banner, and play-disable all track edits
  const [thumbVersion, bumpThumbs] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    // debounced like FlipbookBar's: onChange fires on every pointermove of a
    // stroke, and each bump re-renders the visible thumbnails
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = excalidrawAPI.onChange(() => {
      if (timer === null) {
        timer = setTimeout(() => {
          timer = null;
          bumpThumbs();
        }, 250);
      }
    });
    return () => {
      unsub();
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [excalidrawAPI]);

  // while this panel is mounted, the flipbook is active even if the bottom bar
  // is closed — lets FlipbookBar's onion effect render from here
  useEffect(() => {
    setFlipbookActive(true);
    return () => setFlipbookActive(false);
  }, [setFlipbookActive]);

  const elements = excalidrawAPI?.getSceneElements() ?? [];
  const books = getFlipbookBooks(elements);
  const bookId = resolveBookId(elements, activeBookId);
  const pages = getFlipbookPages(elements, bookId ?? undefined);
  const activeIndex = Math.max(
    0,
    pages.findIndex((f) => f.id === activeId),
  );

  // point onion/selection at the first page on entry when nothing is active yet
  useEffect(() => {
    if (!activeId && pages.length > 0) {
      setActiveId(pages[0].id);
    }
    // run once per mount against the initial scene
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!excalidrawAPI) {
    return null;
  }

  const screens = getScreens(elements);
  const canBake = screens.length > 0 && pages.length > 0;
  // only offered while the scene has no flipbook at all — an empty book that
  // the user just created is not an invitation to swallow every screen
  const adoptable = books.length === 0 ? planAdoptFrames(elements) : [];
  const menuFrame = menu ? pages.find((f) => f.id === menu.frameId) : null;

  const bakeInto = (screen: ExcalidrawFrameLikeElement) => {
    const result = applyBake(excalidrawAPI, screen, fps, bookId ?? undefined);
    setScreenPicker(null);
    if (result) {
      excalidrawAPI.setViewport({ target: [screen], fit: "scale-down" });
      setBakeNotice(null);
    } else {
      setBakeNotice("Nothing to copy");
    }
  };

  const adopt = () => {
    for (const frame of planAdoptFrames(excalidrawAPI.getSceneElements())) {
      excalidrawAPI.mutateElement(frame, {
        customData: flipbookPageCustomData(frame.customData),
      });
    }
    captureUndo();
  };

  // Export itself lives in the "Screens & export" tab — one surface, one code
  // path, and this book gets every option (fps, background, loop, quality,
  // filename) instead of the two it used to have here.
  const onExport = () => {
    if (pages.length === 0 || !bookId) {
      return;
    }
    setExportTargetId(bookTargetId(bookId));
    excalidrawAPI.toggleSidebar({
      name: "default",
      tab: "settings",
      force: true,
    });
  };

  return (
    <div className="flipbook-panel">
      {adoptable.length > 0 && (
        <div className="flipbook-panel__adopt">
          <span>
            Adopt {adoptable.length} existing frame
            {adoptable.length === 1 ? "" : "s"} as flipbook pages?
          </span>
          <button className="flipbook-panel__adopt-btn" onClick={adopt}>
            Adopt
          </button>
        </div>
      )}

      <section className="flipbook-panel__section">
        <div
          className={
            confirmDeleteBook === bookId && bookId
              ? "flipbook-panel__confirm"
              : "flipbook-panel__books"
          }
        >
          {confirmDeleteBook === bookId && bookId ? (
            <>
              <span className="flipbook-panel__confirm-text">
                Delete this flipbook and its {pages.length}{" "}
                {pages.length === 1 ? "page" : "pages"}?
              </span>
              <button
                className="flipbook-panel__book-remove flipbook-panel__book-remove--confirm"
                onClick={() => {
                  removeBook(bookId);
                  setConfirmDeleteBook(null);
                }}
              >
                Delete
              </button>
              <button
                className="flipbook-panel__book-cancel"
                onClick={() => setConfirmDeleteBook(null)}
              >
                Cancel
              </button>
            </>
          ) : (
            // one control instead of select + "+ Flipbook" + "×": a native
            // <select> renders its list with the OS, which can't follow the
            // panel's dark material, and the row's three controls squeezed the
            // book name down to a few characters. New/Delete live in the menu.
            <button
              ref={bookMenuAnchor}
              className="flipbook-panel__book-picker"
              aria-haspopup="menu"
              aria-expanded={bookMenu}
              title="Switch, add or delete a flipbook"
              onClick={() => setBookMenu((open) => !open)}
            >
              <span className="flipbook-panel__book-picker-name">
                {books.find((b) => b.id === bookId)?.name ?? "No flipbook yet"}
              </span>
              {bookId && (
                <span className="flipbook-panel__book-picker-meta">
                  {pages.length} {pages.length === 1 ? "page" : "pages"}
                </span>
              )}
              <svg
                className="flipbook-panel__book-picker-chevron"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3.5 5L6 7.5 8.5 5" />
              </svg>
            </button>
          )}
        </div>
        <div className="flipbook-panel__pages">
          <div className="flipbook-panel__strip">
            {pages.map((frame, index) => (
              <FrameThumb
                key={frame.id}
                frame={frame}
                index={index}
                active={index === activeIndex}
                dropTarget={dropIndex === index && dragId !== frame.id}
                appState={appState}
                excalidrawAPI={excalidrawAPI}
                thumbVersion={thumbVersion}
                withGhostsSuspended={withGhostsSuspended}
                onClick={() => goTo(frame, true)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ frameId: frame.id, x: e.clientX, y: e.clientY });
                }}
                onDragStart={() => setDragId(frame.id)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropIndex(index);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragId) {
                    reorderPages(dragId, index);
                  }
                  setDragId(null);
                  setDropIndex(null);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setDropIndex(null);
                }}
              />
            ))}
          </div>
          {/* outside the scroller so it stays reachable at any page count */}
          <button
            className="flipbook-panel__add"
            title="Add page after current"
            onClick={() => addPage(pages[activeIndex])}
          >
            <svg
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M7 2.5v9M2.5 7h9" />
            </svg>
          </button>
        </div>
        <p className="flipbook-panel__hint">
          Drag to reorder · right-click for more
        </p>
      </section>

      <section className="flipbook-panel__section">
        <h3>Playback</h3>
        <button
          className={`flipbook-panel__play${
            playing ? " flipbook-panel__play--playing" : ""
          }`}
          disabled={pages.length < 2}
          onClick={() => setPlaying(!playing)}
        >
          {playing ? (
            <svg
              className="flipbook-panel__btn-icon"
              viewBox="0 0 12 12"
              fill="currentColor"
              aria-hidden="true"
            >
              <rect x="2.5" y="2.5" width="7" height="7" rx="1" />
            </svg>
          ) : (
            <svg
              className="flipbook-panel__btn-icon"
              viewBox="0 0 12 12"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M3.8 2.06a.5.5 0 0 0-.8.4v7.08a.5.5 0 0 0 .8.4l4.9-3.54a.5.5 0 0 0 0-.8L3.8 2.06Z" />
            </svg>
          )}
          {playing ? "Stop" : "Play"}
        </button>
        <label htmlFor="flipbook-fps">
          <span>Frames per second</span>
          <Stepper
            id="flipbook-fps"
            value={fps}
            min={1}
            max={30}
            onValue={setFps}
            onCommit={applyFps}
          />
        </label>
        <label>
          <span>Repeat</span>
          <select
            value={playMode}
            onChange={(e) => setPlayMode(e.target.value as PlayMode)}
          >
            {PLAY_MODES.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        <button
          className="flipbook-panel__ghost-btn"
          title="Show the bottom filmstrip"
          onClick={() => setOpen(true)}
        >
          Show filmstrip
        </button>
      </section>

      <section className="flipbook-panel__section">
        <h3>Onion skin</h3>
        <label className="flipbook-panel__toggle-row">
          <span>Show neighbouring pages</span>
          <input
            className="hig-switch"
            type="checkbox"
            checked={onion.enabled}
            onChange={(e) => setOnion({ ...onion, enabled: e.target.checked })}
          />
        </label>
        <label htmlFor="flipbook-onion-before">
          <span>Pages before</span>
          <Stepper
            id="flipbook-onion-before"
            value={onion.before}
            min={0}
            max={3}
            disabled={!onion.enabled}
            onValue={(v) => setOnion({ ...onion, before: v })}
          />
        </label>
        <label htmlFor="flipbook-onion-after">
          <span>Pages after</span>
          <Stepper
            id="flipbook-onion-after"
            value={onion.after}
            min={0}
            max={3}
            disabled={!onion.enabled}
            onValue={(v) => setOnion({ ...onion, after: v })}
          />
        </label>
        <label title="Past pages red, future pages green">
          <span>Colour tint</span>
          <input
            className="hig-switch"
            type="checkbox"
            checked={onion.tint}
            disabled={!onion.enabled}
            onChange={(e) => setOnion({ ...onion, tint: e.target.checked })}
          />
        </label>
      </section>

      <section className="flipbook-panel__section">
        <h3>Export</h3>
        <button
          className="flipbook-panel__cta"
          disabled={pages.length === 0}
          title={
            pages.length === 0
              ? "Add a page first"
              : "Open this flipbook in the Screens & export tab"
          }
          onClick={onExport}
        >
          <svg
            className="flipbook-panel__btn-icon"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 1.5v6M3.5 5.5 6 8l2.5-2.5M2 10.5h8" />
          </svg>
          Export animation…
        </button>
        <button
          className="flipbook-panel__ghost-btn"
          disabled={!canBake}
          title={
            screens.length === 0
              ? "Create a screen first"
              : pages.length === 0
              ? "Add a page first"
              : "Copy the flipbook onto a screen as a baked animation"
          }
          onClick={(e) => {
            setBakeNotice(null);
            // ponytail: with a single screen just bake into it; with several,
            // always show the picker rather than guessing which screen the
            // viewport happens to be centred on — that inference is invisible
            // to the user, and a two-click picker is cheaper than a wrong bake.
            if (screens.length === 1) {
              bakeInto(screens[0]);
            } else {
              setScreenPicker({ x: e.clientX, y: e.clientY });
            }
          }}
        >
          Copy to screen
        </button>
        {bakeNotice && <p className="flipbook-panel__notice">{bakeNotice}</p>}
      </section>

      {bookMenu && (
        <FloatingMenu
          x={bookMenuOrigin().x}
          y={bookMenuOrigin().y}
          placement="below"
          minWidth={bookMenuAnchor.current?.offsetWidth}
          onClose={closeBookMenu}
        >
          {books.map((book) => (
            <button
              key={book.id}
              className={
                book.id === bookId ? "flipbook-menu__item--checked" : undefined
              }
              onClick={() => {
                setActiveBookId(book.id);
                // the new book's own first page becomes current
                setActiveId(null);
              }}
            >
              <span>{book.name}</span>
              <span className="flipbook-menu__meta">
                {book.pageCount} {book.pageCount === 1 ? "page" : "pages"}
              </span>
            </button>
          ))}
          <div className="flipbook-menu__sep" />
          <button onClick={addBook}>New flipbook</button>
          {books.length > 1 && bookId && (
            <button
              className="flipbook-menu__danger"
              onClick={() => setConfirmDeleteBook(bookId)}
            >
              Delete this flipbook
            </button>
          )}
        </FloatingMenu>
      )}

      {screenPicker && (
        <FloatingMenu
          x={screenPicker.x}
          y={screenPicker.y}
          onClose={closeScreenPicker}
        >
          {screens.map((screen, i) => (
            <button key={screen.id} onClick={() => bakeInto(screen)}>
              {screen.name || `Screen ${i + 1}`}
            </button>
          ))}
        </FloatingMenu>
      )}

      {menu && menuFrame && (
        <FloatingMenu x={menu.x} y={menu.y} onClose={closeMenu}>
          <button onClick={() => duplicatePage(menuFrame)}>
            Duplicate page
          </button>
          <button
            onClick={() => removeIds(clearPageIds(elements, menuFrame.id))}
          >
            Clear page
          </button>
          <button
            className="flipbook-menu__danger"
            onClick={() => {
              removeIds(deletePageIds(elements, menuFrame.id));
              if (activeId === menuFrame.id) {
                setActiveId(null);
              }
            }}
          >
            Delete page
          </button>
        </FloatingMenu>
      )}
    </div>
  );
};
